import ErrorHandler from '@js/ErrorHandler';
import { formatBlacklistErrorMessage, parseApiResponseErrors } from '@js/parse-api-errors';

export { formatBlacklistErrorMessage } from '@js/parse-api-errors';

export const PAY_CART_DEFAULT_ERROR_MESSAGE =
	'Something went wrong, please contact customer support at support@sculptnation.com to place your order or try again.';

export const PAYMENT_FORM_RESTORE_ERROR_MESSAGE =
	'We could not reload the payment form. Please refresh the page and try again.';

function prefixForErrorKey(key) {
	if (key.startsWith('billing_address')) {
		return 'Billing: ';
	}

	if (key.startsWith('shipping_address')) {
		return 'Shipping: ';
	}

	return '';
}

export function collectPayCartResponseErrors(error, defaultMessage) {
	if (error?.response?.status === 451 && error?.response?.data?.errors?.message) {
		return {
			replace: false,
			messages: [formatBlacklistErrorMessage(error.response.data.errors.message)],
		};
	}

	if (error?.response?.data?.errors) {
		const responseErrors = error.response.data.errors;
		const messages = [];

		Object.keys(responseErrors).forEach((key) => {
			const prefix = prefixForErrorKey(key);
			const values = Array.isArray(responseErrors[key])
				? responseErrors[key]
				: [responseErrors[key]];

			values.forEach((err) => {
				if (err !== null && err !== undefined && err !== '') {
					messages.push(prefix + err);
				}
			});
		});

		return { replace: false, messages };
	}

	const messages = parseApiResponseErrors(error, defaultMessage);

	if (messages === null) {
		ErrorHandler.reportError('Invalid server response!', error);
	}

	return {
		replace: true,
		messages: messages === null ? [defaultMessage] : messages,
	};
}

export function applyPayCartHttpErrors(commit, error, defaultMessage = PAY_CART_DEFAULT_ERROR_MESSAGE) {
	const { messages } = collectPayCartResponseErrors(error, defaultMessage);

	commit('setErrors', messages);
	window.scrollTo({
		top: 0,
		behavior: 'smooth',
	});
}

import { isAdyenGateway } from '@js/mixins/adyen-three-ds-billing';

export { isAdyenGateway };

export function payCartResponseRequiresAdyenThreeDs(response, gateway) {
	return isAdyenGateway(gateway)
		&& response?.meta?.payment?.requires_action === true
		&& !!response?.meta?.payment?.action;
}

/**
 * Handle a successful payCart API response: start Adyen 3DS or redirect to next_url.
 *
 * @returns {Promise<{ handled: boolean, threeDs: boolean }>}
 */
export async function finishPayCartResponse(response, {
	gateway,
	handleAdyenThreeDsAction,
	onBeforeThreeDs,
	redirectToNextUrl,
}) {
	if (payCartResponseRequiresAdyenThreeDs(response, gateway)) {
		if (typeof handleAdyenThreeDsAction !== 'function') {
			throw new Error('Card form is no longer available. Please refresh the page and try again.');
		}

		onBeforeThreeDs?.();
		await handleAdyenThreeDsAction(response.meta.payment.action);

		return { handled: true, threeDs: true };
	}

	const nextUrl = response?.meta?.next_url;
	if (nextUrl) {
		redirectToNextUrl(nextUrl);

		return { handled: true, threeDs: false };
	}

	return { handled: false, threeDs: false };
}

export async function restorePaymentFormIfAvailable(state) {
	if (typeof state.restorePaymentForm !== 'function') {
		return { success: true };
	}

	try {
		await state.restorePaymentForm();
		return { success: true };
	} catch (error) {
		console.error('[Checkout] Failed to restore payment form:', error);
		return {
			success: false,
			message: error?.message || PAYMENT_FORM_RESTORE_ERROR_MESSAGE,
		};
	}
}
